import React, {Ref, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {IInnerWindowController, InnerWindowContext} from '../windows';
import {FloatingWindow, FloatingWindowProps, FloatingWindowRenderProps} from "../floating-window/floating-window";
import {
    assertNotNullable, disposableDisposeAll,
    Evaluable, evaluateFunction,
    evaluateWhenFunction, htmlSameOriginIframeCreateSelector,
    IdGenerator,
    iterableMapToArray, Timer
} from "@wix/devzai-utils-common";
import {reactCreateMemo, reactForwardRef} from "../react-utils/react-utils";
import {UnmountingTaskScheduler, useRenderState} from "../hooks";
import {DomElementResolver} from "../dom-element-resolver/dom-element-resolver";
import {DomEventListener, domNodesListToArray, elementGetOwnerWindow} from "@wix/devzai-utils-dom";

export const FloatingWindowOpener = reactCreateMemo(reactForwardRef(function FloatingWindowOpener<CONTEXT extends {}> (props: FloatingWindowOpener.Props<CONTEXT>) {
    const {
        ref,
        children,
        containerInfo,
        onOpen,
        onClose
    } = props;

    const windowClosingControllerRef = useRef<FloatingWindowOpener.WindowClosingController>(null);
    const [openingContext, setOpeningContext] = useState<{
        openingId: string;
        contextProps: CONTEXT;
        openingOptions?: FloatingWindowOpener.OpeningOptions
    } | null>(null);

    const renderState = useRenderState({
        onOpen: () => {
            onOpen?.();
        },
        onClose: () => {
            onClose?.();

            openingContext?.openingOptions?.onClose?.()

            setOpeningContext(null);
        }
    })

    const floatingWindowOpener = useMemo<FloatingWindowOpener<CONTEXT>>(() => {
        return {
            open (contextProps, openingOptions) {
                setOpeningContext({
                    contextProps: contextProps,
                    openingOptions: openingOptions,
                    openingId: IdGenerator.uniqueId()
                });

                renderState.current.onOpen()

                return this;
            },
            close () {
                const windowClosingController = windowClosingControllerRef.current;
                if (windowClosingController) {
                    windowClosingController.handleWindowClosing(() => {
                        renderState.current.onClose();
                    })
                } else {
                    renderState.current.onClose();
                }
            },
            closeWindowImmediately () {
                renderState.current.onClose();
            }
        }
    }, [renderState]);

    useImperativeHandle(ref, () => floatingWindowOpener, [floatingWindowOpener])

    const innerWindowController = useMemo<IInnerWindowController>(() => {
        return {
            closeWindow: () => {
                floatingWindowOpener.close()
            },
            closeWindowImmediately: () => {
                floatingWindowOpener.closeWindowImmediately();
            }
        }
    }, [floatingWindowOpener])

    if (!openingContext) {
        return null;
    }

    return (
        <InnerWindowContext.Provider
            value={innerWindowController}
        >
            <FloatingWindowWrapper
                containerInfo={containerInfo}
                behaviours={openingContext.openingOptions?.behaviours}
                key={openingContext.openingId}
            >
                {floatingWindowRenderProps => {
                    return evaluateWhenFunction(children, {
                        ...floatingWindowRenderProps,
                        floatingWindowOpener: floatingWindowOpener,
                        contextProps: openingContext.contextProps,
                        windowClosingControllerRef: windowClosingControllerRef
                    })
                }}
            </FloatingWindowWrapper>
        </InnerWindowContext.Provider>
    );
}));

const FloatingWindowWrapper = React.memo(function FloatingWindowWrapper (
    props: FloatingWindowProps & {
        behaviours?: FloatingWindowOpener.OpenWindowBehaviour[];
    }
) {
    const {
        behaviours = [],
        ...floatingWindowProps
    } = props;

    const contentDomElementResolverRef = useRef<DomElementResolver>(null);
    const innerWindowController = assertNotNullable(useContext(InnerWindowContext))

    useEffect(() => {

        const behaviourContext = {
            windowController: innerWindowController,
            getFloatingWindowElement: () => {
                return contentDomElementResolverRef.current?.getDomElement() ?? null;
            }
        } satisfies FloatingWindowOpener.OpenWindowBehaviourContext;
        
        const destructors = iterableMapToArray(behaviours, (autoClosingBehaviour, skip) => {
            const destructor = autoClosingBehaviour(behaviourContext);

            return destructor ? destructor : skip;
        })

        return () => {
            for (const destructor of destructors) {
                destructor();
            }
        }
    }, []);

    return (
        <DomElementResolver
            ref={contentDomElementResolverRef}
        >
            <FloatingWindow
                {...floatingWindowProps}
            />
        </DomElementResolver>
    );
});

export interface FloatingWindowOpener<CONTEXT extends {} = {}> {
    open (
        contextProps: CONTEXT,
        options?: FloatingWindowOpener.OpeningOptions
    ) : this;

    close () : void;

    closeWindowImmediately () : void;
}

export namespace FloatingWindowOpener {


    export interface OpenWindowBehaviourContext {
        windowController: IInnerWindowController;
        getFloatingWindowElement: () => Element | null;
    }

    export type OpenWindowBehaviour = (behaviourContext: OpenWindowBehaviourContext) => (void | (() => void));

    export interface OpeningOptions {
        behaviours?: FloatingWindowOpener.OpenWindowBehaviour[];
        onClose?: () => void;
    }

    export interface WindowClosingController {
        handleWindowClosing (closeCallback: () => void) : void;
    }
    
    export interface WindowRenderProps<CONTEXT extends {}> extends FloatingWindowRenderProps {
        floatingWindowOpener: FloatingWindowOpener<CONTEXT>;
        contextProps: CONTEXT;
        windowClosingControllerRef: React.Ref<WindowClosingController>;
    }

    export interface Props<CONTEXT extends {}> {
        ref?: Ref<FloatingWindowOpener<CONTEXT>>;
        children: Evaluable<(renderProps: WindowRenderProps<CONTEXT>) => React.ReactElement>;
        containerInfo?: FloatingWindowProps['containerInfo'];
        onOpen?: () => void;
        onClose?: () => void;
    }
}

export function floatingWindowOpenerCreateAutoCloseOnTimeoutBehaviour (
    options: {
        timeout: number;
        disableAutoCloseWhenHover?: boolean;
    }
) : FloatingWindowOpener.OpenWindowBehaviour {
    
    const {
        timeout,
        disableAutoCloseWhenHover = false
    } = options;
    
    return (behaviourContext) => {


        const timer = new Timer(timeout, () => {
            behaviourContext.windowController.closeWindow();

            timer.deactivate();
        })

        const eventBindings = evaluateFunction(() => {

            if (disableAutoCloseWhenHover) {
                const domElement = behaviourContext.getFloatingWindowElement();

                if (domElement) {
                    return [
                        DomEventListener.bind(domElement, 'mouseenter', () => {
                            timer.deactivate();
                        }),
                        DomEventListener.bind(domElement, 'mouseleave', () => {
                            timer.activate();
                        }),
                    ]
                }
            }

            return [];
        })

        timer.activate();

        return () => {
            timer.deactivate();
            disposableDisposeAll(eventBindings);
        }
    }
}

export function floatingWindowOpenerCreateCloseOnClickOutsideBehaviour () : FloatingWindowOpener.OpenWindowBehaviour {

    return (behaviourContext) => {


        const domElement = behaviourContext.getFloatingWindowElement();

        if (domElement) {

            const ownerDocument = elementGetOwnerWindow(domElement).document;

            const bindings = [
                DomEventListener.bind(ownerDocument, 'mousedown', (event) => {
                    const eventTarget = event.target as HTMLElement;

                    if (!domElement.contains(eventTarget)) {
                        behaviourContext.windowController.closeWindowImmediately()
                    }
                }),
                ...iterableMapToArray(domNodesListToArray(ownerDocument.querySelectorAll(htmlSameOriginIframeCreateSelector())), (htmlElement, skip) => {

                    const iframe = htmlElement as HTMLIFrameElement;

                    try {
                        const iframeDocument = iframe.contentDocument;

                        if (!iframeDocument) {
                            return skip;
                        }

                        // Add the mousedown listener to the iframe's document
                        return DomEventListener.bind(iframeDocument, 'mousedown', (event) => {
                            const eventTarget = event.target as HTMLElement;

                            if (!domElement.contains(eventTarget)) {
                                behaviourContext.windowController.closeWindowImmediately()
                            }
                        })
                    } catch (error) {
                        // Error handling for cross-origin frames
                        console.error("Cannot attach listener to iframe:", error);
                        return skip;
                    }
                })
            ];

            return () => {
                for (const binding of bindings) {
                    binding.dispose();
                }
            }
        } else {
            return () => {}
        }
    }
}

export function floatingWindowOpenerCreateAutoCloseOnComponentUnmount (
    options: {
        unmountingTaskScheduler: UnmountingTaskScheduler;
    }
) : FloatingWindowOpener.OpenWindowBehaviour {

    const {
        unmountingTaskScheduler
    } = options;

    return (behaviourContext) => {

        const task = unmountingTaskScheduler(() => {
            behaviourContext.windowController.closeWindowImmediately()
        })

        return () => {
            task.dispose();
        }
    }
}

export function floatingWindowOpenerOpenWithClosedPromise<CONTEXT extends {} = {}> (
    floatingWindowOpener: FloatingWindowOpener<CONTEXT>,
    contextProps: CONTEXT,
    options: FloatingWindowOpener.OpeningOptions = {}
) {
    const {
        onClose,
        ...restOptions
    } = options;

    return new Promise((resolve) => {
        floatingWindowOpener.open(contextProps, {
            ...restOptions,
            onClose: () => {
                onClose?.();

                resolve(undefined)
            }
        })
    })

}